// EventStockList — read-only modal showing the real-time stock for a
// single event. Useful for the cashier in POS to know "what's actually
// on the table right now" without opening the admin's Manage Stock UI.
//
// Stock is computed from EventStockAllocation:
//   - qtyIn:  brought to the event
//   - qtySold: incremented per sale, decremented on customer return
//   - qtyReturnedToGudang: filled at event close (0 while event is active)
//   - onHand: qtyIn - qtySold (what's still available to sell)

import { Fragment } from 'react';
import { Package2, AlertTriangle, CheckCheck, PackageCheck } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useEventAllocations } from '@/hooks/useEventAllocations';
import { formatDate, formatNumber } from '@/lib/format';
import { groupAllocationsByProduct } from '@/lib/allocationGroups';
import type { EventStockAllocation } from '@/types/domain';

export interface EventStockListProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  eventName?: string;
}

export function EventStockList({
  open,
  onClose,
  eventId,
  eventName,
}: EventStockListProps) {
  // Only fetch when modal is open — saves reads on the POS hot path.
  const { data: allocations, isLoading } = useEventAllocations(
    open ? eventId : null,
  );

  const rows = (allocations ?? []).map((a) => {
    const onHand = Math.max(0, a.qtyIn - a.qtySold);
    return { ...a, onHand };
  });

  const groups = groupAllocationsByProduct(allocations ?? []);

  const totals = rows.reduce(
    (acc, r) => ({
      qtyIn: acc.qtyIn + r.qtyIn,
      qtySold: acc.qtySold + r.qtySold,
      onHand: acc.onHand + r.onHand,
    }),
    { qtyIn: 0, qtySold: 0, onHand: 0 },
  );

  // Counted per product, not per lot: a product is sold out when all its lots are.
  const soldOut = groups.filter((g) => g.available === 0 && g.qtyIn > 0).length;
  const lowStock = groups.filter((g) => g.available > 0 && g.available < 5).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Stok di event ini"
      description={
        eventName
          ? `Stock real-time untuk "${eventName}".`
          : 'Stock real-time untuk event yang sedang aktif.'
      }
      size="xl"
      footer={<Button variant="ghost" onClick={onClose}>Tutup</Button>}
    >
      {!isLoading && rows.length > 0 && (
        <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryStat label="Total qty in" value={formatNumber(totals.qtyIn)} tone="default" />
          <SummaryStat label="Sold" value={formatNumber(totals.qtySold)} tone="default" />
          <SummaryStat label="On-hand" value={formatNumber(totals.onHand)} tone="primary" />
          <SummaryStat
            label="Sold out / low"
            value={`${soldOut} habis · ${lowStock} menipis`}
            tone={soldOut > 0 ? 'danger' : lowStock > 0 ? 'warning' : 'success'}
          />
        </div>
      )}

      {isLoading ? (
        <LoadingState message="Memuat stok…" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Package2 size={24} strokeWidth={1.5} />}
          title="Belum ada alokasi stok"
          description="Owner belum set alokasi untuk event ini. Tambah dari halaman Events → Manage Stock."
        />
      ) : (
        <div className="max-h-[55vh] overflow-y-auto overscroll-contain -mx-1 px-1">
          <Table>
            <THead>
              <TR>
                <TH>Produk</TH>
                <TH className="text-right">Qty in</TH>
                <TH className="text-right">Sold</TH>
                <TH className="text-right">On-hand</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {groups.map((g) => {
                const isSoldOut = g.available === 0;
                const isLow = g.available > 0 && g.available < 5;
                return (
                  <Fragment key={g.productId}>
                    <TR className={g.lots.length > 1 ? 'bg-canvas/60' : undefined}>
                      <TD>
                        <p className="font-medium text-ink">{g.productName}</p>
                        <code className="text-caption font-mono text-ink-subtle">{g.productSku}</code>
                        {g.lots.length === 1 && <LotLine a={g.lots[0]} />}
                      </TD>
                      <TD className="text-right tabular-nums">{formatNumber(g.qtyIn)}</TD>
                      <TD className="text-right tabular-nums">{formatNumber(g.qtySold)}</TD>
                      <TD className="text-right tabular-nums font-medium">{formatNumber(g.available)}</TD>
                      <TD>
                        {isSoldOut ? (
                          <Chip variant="danger">Habis</Chip>
                        ) : isLow ? (
                          <Chip variant="warning">Menipis</Chip>
                        ) : (
                          <Chip variant="success">
                            <CheckCheck size={11} />
                            Tersedia
                          </Chip>
                        )}
                      </TD>
                    </TR>
                    {g.lots.length > 1 &&
                      g.lots.map((a) => (
                        <TR key={a.id}>
                          <TD className="!pl-8"><LotLine a={a} /></TD>
                          <TD className="text-right tabular-nums text-ink-subtle">{formatNumber(a.qtyIn)}</TD>
                          <TD className="text-right tabular-nums text-ink-subtle">{formatNumber(a.qtySold)}</TD>
                          <TD className="text-right tabular-nums">{formatNumber(Math.max(0, a.qtyIn - a.qtySold))}</TD>
                          <TD />
                        </TR>
                      ))}
                  </Fragment>
                );
              })}
            </TBody>
            <tfoot>
              <TR className="bg-canvas/50 font-medium">
                <TD className="text-overline uppercase text-ink-subtle font-medium">
                  Total
                </TD>
                <TD className="text-right tabular-nums">
                  {formatNumber(totals.qtyIn)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatNumber(totals.qtySold)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatNumber(totals.onHand)}
                </TD>
                <TD />
              </TR>
            </tfoot>
          </Table>
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <div className="mt-4 p-3 rounded border border-line bg-canvas/50 text-caption text-ink-subtle flex items-start gap-2">
          <PackageCheck size={14} className="text-ink-subtle shrink-0 mt-0.5" />
          <div>
            <p className="text-ink font-medium mb-0.5">Cara baca angka ini</p>
            <p>
              <strong>Qty in</strong> = yang dibawa ke event. <strong>Sold</strong> =
              yang sudah terjual (auto-decrement pas return). <strong>On-hand</strong> =
              qty in − sold = yang masih bisa dijual. Saat event ditutup, owner
              akan isi &ldquo;Kembali ke gudang&rdquo; — selisihnya jadi loss.
            </p>
            {soldOut > 0 && (
              <p className="mt-2 inline-flex items-center gap-1 text-danger">
                <AlertTriangle size={12} />
                {soldOut} produk habis. Hubungi owner untuk tambah alokasi kalau masih
                ada stok di gudang.
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'default' | 'primary' | 'success' | 'warning' | 'danger';
}) {
  const toneClass = {
    default: 'text-ink',
    primary: 'text-primary',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  }[tone];
  return (
    <div className="p-3 rounded-lg bg-canvas border border-line">
      <p className="text-overline uppercase text-ink-subtle font-medium">
        {label}
      </p>
      <p className={`text-subhead font-display font-semibold mt-1 tabular-nums ${toneClass}`}>
        {value}
      </p>
    </div>
  );
}

function LotLine({ a }: { a: EventStockAllocation }) {
  if (!a.lotNumber && !a.expiryDate) return null;
  const expired = !!a.expiryDate && a.expiryDate < Date.now();
  return (
    <p className="text-caption text-ink-subtle flex flex-wrap gap-x-2">
      <span>Lot <span className="font-mono text-ink">{a.lotNumber || '—'}</span></span>
      {a.expiryDate && (
        <span className={expired ? 'text-danger font-medium' : undefined}>ED {formatDate(a.expiryDate)}</span>
      )}
    </p>
  );
}
